import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  coversApi,
  uploadToSignedUrl,
  type Cover,
  type CoverCategory,
  type TextZone,
} from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CoverThumbnail } from '@/components/CoverThumbnail';
import type { TextZoneOverride, CoverDesignTransform } from '@/components/CoverThumbnail';
import { extractDominantColor, bakeDesignToPng } from '@/lib/imageUtils';
import { foldZoneScaleOverrides } from '@/lib/coverZoneBake';
import { EDITOR_TEXT_FONTS, DEFAULT_COVER_FONT } from '@/lib/editorFonts';
import { cn } from '@/lib/utils';

const FALLBACK_BG = '#0E2A47';

const ZONE_TYPE_LABELS: Record<TextZone['type'], string> = {
  destination: 'Destination',
  year: 'Année',
  custom: 'Texte libre',
};

const ZONE_TYPE_DOT: Record<TextZone['type'], string> = {
  destination: 'bg-blue-500',
  year: 'bg-amber-500',
  custom: 'bg-purple-500',
};

// Matches the worker's /covers/from-extracted defaults so new manual uploads
// land where the AI extractor would have placed them.
const ZONE_DEFAULTS: Record<TextZone['type'], TextZone> = {
  destination: {
    type: 'destination',
    x: 0.10, y: 0.06, width: 0.83, height: 0.19,
    font_size: 410,
    font_family: DEFAULT_COVER_FONT.family,
    color: '#ffffff',
    align: 'center',
    default_text: 'DESTINATION',
  },
  year: {
    type: 'year',
    x: 0.70, y: 0.22, width: 0.18, height: 0.08,
    font_size: 170,
    font_family: DEFAULT_COVER_FONT.family,
    color: '#ffffff',
    align: 'center',
    default_text: '2024',
  },
  custom: {
    type: 'custom',
    x: 0.20, y: 0.45, width: 0.60, height: 0.10,
    font_size: 120,
    font_family: DEFAULT_COVER_FONT.family,
    color: '#ffffff',
    align: 'center',
    default_text: 'Texte',
  },
};

interface UploadCoverDialogProps {
  isOpen: boolean;
  onClose: () => void;
  categories: CoverCategory[];
}

export function UploadCoverDialog({ isOpen, onClose, categories }: UploadCoverDialogProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  const [extractedColor, setExtractedColor] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [bgColor, setBgColor] = useState<string>(FALLBACK_BG);
  const [isVisible, setIsVisible] = useState(true);
  const [zones, setZones] = useState<TextZone[]>([]);
  // Live corner-resize multipliers, folded into font_size once at save. Baking
  // per-mousemove compounds the scale and makes the text jump — see
  // foldZoneScaleOverrides / CoverEditDialog for the rationale.
  const [zoneOverrides, setZoneOverrides] = useState<Record<number, TextZoneOverride>>({});

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [designTransform, setDesignTransform] = useState<CoverDesignTransform>({ x: 0, y: 0, scale: 1 });
  const [designSelected, setDesignSelected] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Object URLs leak unless revoked. Track + revoke when the file changes
  // or the dialog closes.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function toggleCategory(slug: string) {
    setSelectedCategories((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  }

  function resetAll() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setDimensions(null);
    setExtractedColor(null);
    setName('');
    setSelectedCategories([]);
    setBgColor(FALLBACK_BG);
    setIsVisible(true);
    setZones([]);
    setZoneOverrides({});
    setSelectedKey(null);
    setEditingKey(null);
    setDesignTransform({ x: 0, y: 0, scale: 1 });
    setDesignSelected(false);
    setUploading(false);
    setProgress(0);
    setError(null);
  }

  function handleClose() {
    resetAll();
    onClose();
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));

    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));

    if (f.type.startsWith('image/')) {
      try {
        const extracted = await extractDominantColor(f);
        setExtractedColor(extracted.color);
        setBgColor(extracted.color);
        setDimensions({ width: extracted.width, height: extracted.height });
      } catch {
        // non-fatal
      }
    }
  }

  function updateZone(i: number, patch: Partial<TextZone>) {
    setZones((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }

  function applyOverrideToZone(i: number, ovr: TextZoneOverride) {
    // scale is a multiplier — never bake it per-move (it would compound). Keep
    // it live in zoneOverrides; foldZoneScaleOverrides collapses it at save.
    if (ovr.scale !== undefined) {
      setZoneOverrides((prev) => ({ ...prev, [i]: { ...prev[i], scale: ovr.scale } }));
    }
    setZones((prev) => {
      const next = [...prev];
      const z = next[i];
      if (!z) return prev;
      const patch: Partial<TextZone> = {};
      if (ovr.x !== undefined) patch.x = ovr.x;
      if (ovr.y !== undefined) patch.y = ovr.y;
      if (ovr.color !== undefined) patch.color = ovr.color;
      if (ovr.fontFamily !== undefined) patch.font_family = ovr.fontFamily;
      if (ovr.fontSize !== undefined) patch.font_size = ovr.fontSize;
      if (Object.keys(patch).length === 0) return prev;
      next[i] = { ...z, ...patch };
      return next;
    });
  }

  function addZone(type: TextZone['type']) {
    setZones((prev) => {
      const next = [...prev, ZONE_DEFAULTS[type]];
      setSelectedKey(String(next.length - 1));
      return next;
    });
  }

  function deleteZone(i: number) {
    setZones((prev) => prev.filter((_, idx) => idx !== i));
    // Keep the index-keyed overrides aligned after the splice.
    setZoneOverrides((prev) => {
      const next: Record<number, TextZoneOverride> = {};
      for (const [k, v] of Object.entries(prev)) {
        const idx = Number(k);
        if (idx === i) continue;
        next[idx > i ? idx - 1 : idx] = v;
      }
      return next;
    });
    setSelectedKey(null);
    setEditingKey(null);
  }

  // Build a synthetic Cover so CoverThumbnail can render the unsaved design.
  // dominant_color drives the bg, design_url shows the uploaded PNG, text_zones
  // drives the overlaid text.
  const previewCover = useMemo<Cover>(() => ({
    id: '__preview__',
    name: name || 'Nouvelle couverture',
    categories: selectedCategories,
    product_types: ['travel'],
    storage_path: null,
    thumbnail_url: null,
    design_url: previewUrl,
    format: 'PNG',
    text_zones: zones,
    dominant_color: bgColor,
    width_px: dimensions?.width ?? null,
    height_px: dimensions?.height ?? null,
    back_cover_style: 'solid',
    description: null,
    search_tags: null,
    usage_count: 0,
    is_visible: isVisible,
    sort_order: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }), [name, selectedCategories, previewUrl, zones, bgColor, dimensions, isVisible]);

  // Map zone types → strings the CoverThumbnail expects.
  const destZone = zones.find((z) => z.type === 'destination');
  const yearZone = zones.find((z) => z.type === 'year');
  const customTexts: Record<number, string> = {};
  zones.forEach((z, i) => {
    if (z.type === 'custom') customTexts[i] = z.default_text ?? '';
  });

  const selectedIdx = selectedKey !== null ? parseInt(selectedKey, 10) : null;
  const selectedZone = selectedIdx !== null ? zones[selectedIdx] : null;

  const create = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choisissez un fichier');
      if (!name.trim()) throw new Error('Donnez un nom à la couverture');
      if (selectedCategories.length === 0) throw new Error('Sélectionnez au moins une catégorie');
      setUploading(true);
      setError(null);

      // If the admin repositioned the design, bake the transform into a fresh
      // PNG so customers see it pre-positioned. Identity transform (no drag/
      // zoom) skips the bake to preserve the source resolution.
      const transformed =
        designTransform.x !== 0 || designTransform.y !== 0 || designTransform.scale !== 1;
      let uploadBlob: Blob = file;
      let uploadType = file.type;
      let uploadWidth = dimensions?.width ?? null;
      let uploadHeight = dimensions?.height ?? null;
      const uploadName = transformed
        ? file.name.replace(/\.[^.]+$/, '') + '_baked.png'
        : file.name;

      if (transformed) {
        const baked = await bakeDesignToPng(file, designTransform);
        uploadBlob = baked.blob;
        uploadType = 'image/png';
        uploadWidth = baked.width;
        uploadHeight = baked.height;
      }

      const signed = await coversApi.getUploadUrl(uploadName, uploadType);
      const uploadFile = new File([uploadBlob], uploadName, { type: uploadType });
      await uploadToSignedUrl(signed.upload_url, uploadFile, uploadType, setProgress);

      return coversApi.create({
        name: name.trim(),
        categories: selectedCategories,
        product_types: ['travel'],
        storage_path: signed.storage_path,
        // Both URLs point at the uploaded PNG. design_url is what CoverThumbnail
        // uses for compositing (bg + transparent design + text overlays);
        // thumbnail_url is the fallback for any caller that hasn't migrated.
        design_url: signed.public_url,
        thumbnail_url: signed.public_url,
        format: 'PNG',
        dominant_color: bgColor,
        width_px: uploadWidth,
        height_px: uploadHeight,
        is_visible: isVisible,
        // Fold the live per-zone scale overrides into font_size exactly once,
        // here at save. Holding scale as an override (instead of baking
        // font_size on every resize mousemove) is what stops the compounding
        // explosion the admin saw.
        text_zones: foldZoneScaleOverrides(zones, zoneOverrides),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-covers'] });
      handleClose();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
      setUploading(false);
    },
  });

  // Handler for inline text editing on the preview — writes default_text on the
  // zone so the cover ships with the admin's chosen placeholder.
  function handleZoneTextChange(i: number, newText: string) {
    updateZone(i, { default_text: newText });
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="full">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Ajouter une couverture</h2>
          <p className="text-sm text-gray-500">
            Téléversez un design PNG transparent, choisissez la couleur de fond et placez le texte.
          </p>
        </div>
        <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-0 max-h-[78vh]">
        {/* Live preview */}
        <div className="p-6 bg-gray-50 overflow-auto flex items-start justify-center">
          <div className="w-full max-w-md">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            {previewUrl ? (
              <CoverThumbnail
                cover={previewCover}
                className="rounded-xl shadow-lg"
                bgColorOverride={bgColor}
                destinationText={destZone?.default_text ?? ''}
                yearText={yearZone?.default_text ?? ''}
                customTexts={customTexts}
                interactive
                zoneOverrides={zoneOverrides}
                selectedZone={selectedKey}
                editingZone={editingKey}
                onZoneSelect={setSelectedKey}
                onZoneEditChange={setEditingKey}
                onZoneChange={applyOverrideToZone}
                onZoneTextChange={handleZoneTextChange}
                onZoneDelete={deleteZone}
                designTransform={designTransform}
                onDesignTransformChange={setDesignTransform}
                designSelected={designSelected}
                onDesignSelectedChange={setDesignSelected}
              />
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="aspect-[3/4] w-full border-2 border-dashed border-gray-300 hover:border-brand-500 hover:bg-white rounded-xl flex flex-col items-center justify-center transition-colors"
              >
                <div className="text-4xl mb-2">📁</div>
                <p className="font-medium text-gray-700">Choisir un PNG</p>
                <p className="text-xs text-gray-400 mt-1">PNG transparent (max 10 Mo)</p>
              </button>
            )}

            {previewUrl && (
              <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                <span className="font-mono truncate">{file?.name}</span>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-brand-500 hover:underline shrink-0 ml-2"
                  disabled={uploading}
                >
                  Changer
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={handleFileSelect}
              className="hidden"
            />

            {uploading && (
              <div className="mt-3">
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-gray-500 mt-1">{progress}% téléversé</p>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-4 leading-relaxed">
              <strong className="text-gray-600">Design :</strong> clic + glisser pour déplacer,
              roulette ou poignées d'angle pour redimensionner.
              <br />
              <strong className="text-gray-600">Texte :</strong> clic = sélection, glisser pour
              repositionner, double-clic pour éditer.
            </p>

            {previewUrl && (designTransform.x !== 0 || designTransform.y !== 0 || designTransform.scale !== 1) && (
              <button
                type="button"
                onClick={() => setDesignTransform({ x: 0, y: 0, scale: 1 })}
                className="mt-2 text-xs text-brand-500 hover:underline"
              >
                Réinitialiser la position du design
              </button>
            )}
          </div>
        </div>

        {/* Controls */}
        <div className="border-l border-gray-100 overflow-auto p-5 space-y-5">
          <Field label="Nom">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Bali Tropical Sunset"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              disabled={uploading}
            />
          </Field>

          <Field label="Catégories">
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <label
                  key={cat.slug}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(cat.slug)}
                    onChange={() => toggleCategory(cat.slug)}
                    disabled={uploading}
                    className="w-4 h-4 accent-brand-500"
                  />
                  <span>{cat.icon} {cat.label_fr}</span>
                </label>
              ))}
            </div>
            {selectedCategories.length === 0 && (
              <p className="text-xs text-red-600 mt-1.5">Sélectionnez au moins une catégorie.</p>
            )}
          </Field>

          <Field label="Couleur de fond">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="w-12 h-9 rounded border border-gray-200 cursor-pointer"
                disabled={uploading}
              />
              <input
                type="text"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono"
                disabled={uploading}
              />
              {extractedColor && bgColor !== extractedColor && (
                <button
                  type="button"
                  onClick={() => setBgColor(extractedColor)}
                  className="text-xs text-gray-500 hover:text-gray-700 underline shrink-0"
                  disabled={uploading}
                  title="Restaurer la couleur extraite du PNG"
                >
                  Auto
                </button>
              )}
            </div>
          </Field>

          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
              className="w-4 h-4 accent-brand-500"
              disabled={uploading}
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Visible pour les clients</p>
              <p className="text-xs text-gray-500">
                Les clients verront cette couverture dans la galerie
              </p>
            </div>
          </label>

          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-900">
                Calques de texte ({zones.length})
              </h3>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              <Button variant="secondary" size="sm" onClick={() => addZone('destination')} disabled={uploading}>
                + Destination
              </Button>
              <Button variant="secondary" size="sm" onClick={() => addZone('year')} disabled={uploading}>
                + Année
              </Button>
              <Button variant="secondary" size="sm" onClick={() => addZone('custom')} disabled={uploading}>
                + Texte libre
              </Button>
            </div>

            {zones.length === 0 ? (
              <div className="border border-dashed border-gray-200 rounded-xl p-4 text-center text-xs text-gray-400">
                Pas encore de texte. Ajoutez un calque pour commencer.
              </div>
            ) : (
              <div className="space-y-2">
                {zones.map((zone, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedKey(String(i))}
                    className={cn(
                      'w-full text-left p-2.5 rounded-lg border text-sm flex items-center gap-2 transition-colors',
                      selectedKey === String(i)
                        ? 'border-brand-500 bg-brand-500/5'
                        : 'border-gray-200 hover:bg-gray-50'
                    )}
                  >
                    <span className={cn('w-2 h-2 rounded-full shrink-0', ZONE_TYPE_DOT[zone.type])} />
                    <span className="flex-1 min-w-0 truncate">
                      <span className="font-medium text-gray-700">{ZONE_TYPE_LABELS[zone.type]}</span>
                      {zone.default_text && (
                        <span className="text-gray-400 ml-1.5">— {zone.default_text}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {selectedZone && selectedIdx !== null && (
              <SelectedZonePanel
                zone={selectedZone}
                onChange={(patch) => updateZone(selectedIdx, patch)}
                onDelete={() => deleteZone(selectedIdx)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
        <Button variant="secondary" onClick={handleClose} disabled={uploading}>
          Annuler
        </Button>
        <Button
          onClick={() => create.mutate()}
          disabled={!file || !name.trim() || selectedCategories.length === 0 || uploading}
          loading={uploading}
        >
          {uploading ? 'Téléversement...' : 'Enregistrer la couverture'}
        </Button>
      </div>
    </Modal>
  );
}

function SelectedZonePanel({
  zone,
  onChange,
  onDelete,
}: {
  zone: TextZone;
  onChange: (patch: Partial<TextZone>) => void;
  onDelete: () => void;
}) {
  return (
    <div className="mt-3 border border-gray-200 rounded-xl p-3 space-y-2.5 bg-white">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700">
          Édition: {ZONE_TYPE_LABELS[zone.type]}
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-red-600 hover:text-red-700"
        >
          Supprimer
        </button>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Texte par défaut</label>
        <input
          type="text"
          value={zone.default_text ?? ''}
          onChange={(e) => onChange({ default_text: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm"
        />
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Police</label>
        <select
          value={zone.font_family ?? DEFAULT_COVER_FONT.family}
          onChange={(e) => onChange({ font_family: e.target.value })}
          className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
        >
          {EDITOR_TEXT_FONTS.map((f) => (
            <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Taille (px)</label>
          <input
            type="number"
            min={6}
            max={1200}
            value={zone.font_size ?? 32}
            onChange={(e) => onChange({ font_size: parseInt(e.target.value, 10) || 32 })}
            className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Alignement</label>
          <select
            value={zone.align ?? 'center'}
            onChange={(e) => onChange({ align: e.target.value as TextZone['align'] })}
            className="w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
          >
            <option value="left">Gauche</option>
            <option value="center">Centre</option>
            <option value="right">Droite</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">Couleur</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={zone.color ?? '#ffffff'}
            onChange={(e) => onChange({ color: e.target.value })}
            className="w-10 h-8 rounded border border-gray-200 cursor-pointer"
          />
          <input
            type="text"
            value={zone.color ?? '#ffffff'}
            onChange={(e) => onChange({ color: e.target.value })}
            className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm font-mono"
          />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
